import { isDefined } from "@redotech/util/type";
import { CoverageCalculator, CoverageType } from ".";
import {
  DynamicReturnPricing,
  DynamicReturnTypes,
  PricingRuleSet,
} from "../team";
import { finalSaleReturnsCoverageCalculator } from "./final-sale-returns";
import { packageProtectionCoverageCalculator } from "./package-protection";
import { returnCoverageCalculator } from "./returns";

export function determineRuleSet(
  pricingRuleSets: PricingRuleSet[],
  country: string | null,
) {
  for (const priceSet of pricingRuleSets) {
    if (
      !isDefined(country) ||
      priceSet.countries.some(
        (c) => c.toLowerCase() === country.toLowerCase(),
      ) ||
      priceSet.countries.length == 0
    ) {
      return priceSet;
    }
  }
  return null;
}

export function getApplicableRule(ruleSet: PricingRuleSet, cartTotal: number) {
  if (!cartTotal) {
    return ruleSet.priceBrackets[0];
  }
  for (let i = 0; i < ruleSet.priceBrackets.length; i++) {
    if (i === ruleSet.priceBrackets.length - 1) {
      return ruleSet.priceBrackets[i];
    }

    if (
      cartTotal > ruleSet.priceBrackets[i].pricePoint &&
      cartTotal <= ruleSet.priceBrackets[i + 1].pricePoint
    ) {
      return ruleSet.priceBrackets[i];
    }
  }
  return ruleSet.priceBrackets[ruleSet.priceBrackets.length - 1];
}

export function getProductCalculator(type: CoverageType) {
  let productCalculator: CoverageCalculator;
  switch (type) {
    case "Returns":
      productCalculator = returnCoverageCalculator;
      break;
    case "Package Protection":
      productCalculator = packageProtectionCoverageCalculator;
      break;
    case "Final Sale":
      productCalculator = finalSaleReturnsCoverageCalculator;
      break;
    default:
      productCalculator = returnCoverageCalculator;
      break;
  }
  return productCalculator;
}

export function listsHaveMatchingElement(list1: string[], list2: string[]) {
  return list1.some((key) =>
    list2.some((attribute) => attribute.toLowerCase() === key.toLowerCase()),
  );
}

export function getPricingRulesInLocalCurrency(
  pricingRuleSets: PricingRuleSet[],
  exchangeRate: number,
) {
  return pricingRuleSets.map((ruleSet) => {
    ruleSet.priceBrackets.map((bracket) => {
      if (ruleSet.type === "fixed") {
        bracket.value = bracket.value * exchangeRate;
      }
      bracket.pricePoint = bracket.pricePoint * exchangeRate;
      return bracket;
    });

    return ruleSet;
  });
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function getPackageProtectionPrice({
  cartTotal,
  pricingRuleSets,
  country,
  minPrice,
  maxPrice,
  backupPercentage,
  fixedSubscriptionPrice,
  cartHasSubscriptionItem,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  country: string | undefined | null;
  minPrice: number;
  maxPrice: number;
  backupPercentage: number;
  fixedSubscriptionPrice?: number;
  cartHasSubscriptionItem?: boolean;
}) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  if (cartHasSubscriptionItem && fixedSubscriptionPrice) {
    return fixedSubscriptionPrice;
  }

  if (!pricingRuleSets?.length) {
    return clamp(cartTotal * backupPercentage, minPrice, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(price, minPrice, maxPrice);
  }

  return parseFloat(
    clamp(cartTotal * backupPercentage, minPrice, maxPrice).toFixed(2),
  );
}

export function calculateDynamicReturnsPrice(
  dynamicReturns: DynamicReturnPricing,
  basePrice: number,
  numItems: number,
  cartTotal: number,
) {
  if (numItems === 0) {
    return 0;
  }

  switch (dynamicReturns.type) {
    case DynamicReturnTypes.ITEM_COUNT:
      return parseFloat(
        Math.min(
          basePrice + dynamicReturns.pricePerAdditonalItem * (numItems - 1),
          dynamicReturns.maxPrice,
        ).toFixed(2),
      );
    case DynamicReturnTypes.CART_VALUE:
      return parseFloat(
        getPackageProtectionPrice({
          cartTotal,
          pricingRuleSets: [dynamicReturns.pricingRuleSet],
          country: null,
          minPrice: basePrice,
          maxPrice: dynamicReturns.maxPrice,
          backupPercentage: 0,
        }).toFixed(2),
      );
  }
}

function getCostOfItemCoverage({
  cartTotal,
  pricingRuleSets,
  country,
  maxPrice,
  minPrice,
  backupPercentage,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  country: string | null;
  maxPrice: number | undefined;
  minPrice: number | undefined;
  backupPercentage: number;
}) {
  if (!pricingRuleSets?.length) {
    const cost = cartTotal * backupPercentage;
    return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const cost =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
  }

  const cost = cartTotal * backupPercentage;
  return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
}

export function getFinalSaleReturnsPrice({
  cartTotal,
  pricingRuleSets,
  shippingLabelCoveragePrice,
  country,
  minPrice,
  maxPrice,
  backupPercentage,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  shippingLabelCoveragePrice: number | undefined | null;
  country: string | undefined | null;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  backupPercentage: number;
}) {
  if (
    cartTotal === 0 ||
    country === undefined ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  const costOfItemCoverage = getCostOfItemCoverage({
    cartTotal,
    pricingRuleSets,
    country,
    maxPrice,
    minPrice,
    backupPercentage,
  });
  const costOfShippingLabelCoverage = shippingLabelCoveragePrice ?? 0;
  const totalCoveragePrice = costOfItemCoverage + costOfShippingLabelCoverage;
  return parseFloat(totalCoveragePrice.toFixed(2));
}
